import React, { useState, useEffect,useMemo } from "react";
import { Menu, MenuItem } from "@mui/material";
import IOModal from "./components/modal/IOModal";
import DBModal from "./components/modal/DBModal";
import styles from "./Challenges.module.scss";
import toast from "react-hot-toast";

import { useDispatch, useSelector } from "react-redux";
import { fetchChallenges } from "@/store/slices/ChallangesSlice";
import { type RootState } from "@/store/rootReducer";
import type { AppDispatch } from "@/store/store";
import { createChallengesAPI, fetchChallengesByAPI, updateChallengesByAPI, deleteChallengeAPI } from "@/services/api/Challanges";
import Modal from "@/components/Modal/Modal";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal/ConfirmDeleteModal";
import { MODAL_ID } from "@/lib/constants";
import { setModalOpen } from "@/store/slices/globalSlice";
import Table from "@/components/Table/Table";
import Pagination from "@/components/Table/Pagination";
import SearchBox from "@/components/SearchBox/SearchBox";
import PageLayout from "@/components/PageLayout/PageLayout";
import EditIcon from "@/components/SVGComponents/EditIcon";
import DeleteIcon from "@/components/SVGComponents/DeleteIcon";

const Challenges = () => {
    const dispatch = useDispatch<AppDispatch>();

    const { profile } = useSelector((state: RootState) => state.user);
    const currentUserRole = profile?.role?.toUpperCase();

    const { challenges, totalRecords, loading, error } = useSelector(
        (state: RootState) => state.challanges
    );

    const [openModal, setOpenModal] = useState<null | "io" | "db">(null);
    const [editItem, setEditItem] = useState<any>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [page, setPage] = useState(1);
    const perPage = 10;
    const [deleteConfirmItem, setDeleteConfirmItem] = useState<any>(null);
    const [difficultyLevel, setDifficultyLevel] = useState("");
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const cardData = [
        {
            key: "io",
            title: "Input/output",
            icon: (
                <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ width: "24px", height: "24px" }}
                >
                    <rect x="3" y="6" width="18" height="12" rx="2"></rect>
                    <path d="M6 10h2"></path>
                    <path d="M10 10h2"></path>
                    <path d="M14 10h2"></path>
                    <path d="M6 14h12"></path>
                </svg>
            ),
            desc: "Challenges that can be taken in many languages and require candidates to return correct outputs.",
        },
        {
            key: "db",
            title: "Database",
            icon: (
                <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ width: "24px", height: "24px" }}
                >
                    <ellipse cx="12" cy="5" rx="8" ry="3"></ellipse>
                    <path d="M4 5v7c0 1.7 3.6 3 8 3s8-1.3 8-3V5"></path>
                    <path d="M4 12v7c0 1.7 3.6 3 8 3s8-1.3 8-3v-7"></path>
                </svg>
            ),
            desc: "Challenges that use custom SQL tables and require candidates to return a specified output.",
        },
    ];

    const handleUpload = () => {
        setOpenModal(null);
        setEditItem(null); // ✅ clear edit state after upload
    };

    const handleEdit = async (id: string) => {
        console.log(id);

        try {
            const data = await fetchChallengesByAPI(id);
            setEditItem(data);
            const challengeType = data?.data?.challengeType || data?.challengeType || data?.data?.type || data?.type;
            if (challengeType === "SQL") {
                setOpenModal("db");
            } else {
                setOpenModal("io");
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleDeleteClick = (item: any) => {
        setDeleteConfirmItem(item);
        dispatch(
            setModalOpen({
                id: MODAL_ID.CONFIRM_DELETE,
                data: { userName: item.title, used: item.used },
            })
        );
    };

    const handleDelete = async () => {
        if (!deleteConfirmItem) return;
        try {
            await deleteChallengeAPI(deleteConfirmItem.id || deleteConfirmItem._id);
            toast.success("Challenge deleted successfully");
            dispatch(setModalOpen({ id: null, data: null }));
            dispatch(fetchChallenges({ pageNo: page, perPage, search: debouncedSearch, difficultyLevel }));
        } catch (err) {
            console.error(err);
            toast.error("Failed to delete challenge");
        }
    };

    // const handleUpdate = async () => {
    //     try {
    //         await updateChallengesByAPI(editItem._id, editItem);
    //         alert("Updated successfully 🚀");
    //         dispatch(fetchChallenges()); 
    //     } catch (err) {
    //         alert("Update failed ❌");
    //     }
    // };
    // useEffect(() => {
    //     dispatch(fetchChallenges());
    // }, [dispatch]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setPage(1);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    // Fetch challenges when page or debouncedSearch changes
    useEffect(() => {
        dispatch(fetchChallenges({ pageNo: page, perPage, search: debouncedSearch, difficultyLevel }));
    }, [dispatch, page, debouncedSearch, difficultyLevel]);

    const columns = useMemo(() => {
        const baseColumns = [
            {
                name: "Name",
                selector: (row: any) => row.title,
                sortable: true,
                width: "50%",
            },
            {
                name: "Difficulty",
                cell: (row: any) => (
                    <span
                        className={
                            row.difficultyLevel === "EASY"
                                ? styles.badgeEasy
                                : row.difficultyLevel === "MEDIUM"
                                    ? styles.badgeMedium
                                    : styles.badgeHard
                        }
                    >
                        {row.difficultyLevel}
                    </span>
                ),
                sortable: true,
            },
            {
                name: "Used",
                selector: (row: any) => row.used ? `${row.used} times` : "0 times",
                sortable: true,
            }
        ];

        if (currentUserRole !== "VIEWER") {
            baseColumns.push({
                name: "Actions",
                cell: (row: any) => (
                    <div className={styles.actionGroup}>
                        <button
                            className={`${styles.actionBtn} ${styles.editBtn}`}
                            onClick={() => handleEdit(row.id)}
                            title="Edit"
                        >
                            <EditIcon/>
                        </button>
                        <button
                            className={`${styles.actionBtn} ${styles.deleteBtn}`}
                            onClick={() => handleDeleteClick(row)}
                            title="Delete"
                        >
                            <DeleteIcon />
                        </button>
                    </div>
                ),
                width: "120px",
            } as any);
        }

        return baseColumns;
    }, [currentUserRole, page, debouncedSearch, difficultyLevel]);

    // Hooks MUST be called before any early returns

    return (
        <PageLayout
            title="Challenges"
            preCardContent={
                <div className={styles.cardsRow}>
                    {cardData.map(card => (
                        <div key={card.key} className={styles.card}>
                            <div className={styles.cardIcon}>{card.icon}</div>
                            <h3 className={styles.cardTitle}>{card.title}</h3>
                            <p className={styles.cardDesc}>{card.desc}</p>
                            {currentUserRole !== "VIEWER" && (
                                <button
                                    onClick={() => {
                                        setEditItem(null); // ✅ ensure fresh modal for create
                                        if (card.key === "io") setOpenModal("io");
                                        else if (card.key === "db") setOpenModal("db");
                                    }}
                                    className={styles.createBtn}
                                >
                                    + Create
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            }
            innerTitle="Challenges"
            innerActions={
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <SearchBox
                        value={searchTerm}
                        onChange={(val: string) => setSearchTerm(val)}
                        // placeholder="Search challenges..."
                        width={240}
                        onFilterClick={(e: any) => setAnchorEl(e.currentTarget)}
                    />
                    <Menu
                        open={Boolean(anchorEl)}
                        anchorEl={anchorEl}
                        onClose={() => setAnchorEl(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                        {/* <MenuItem onClick={() => { setDifficultyLevel(""); setAnchorEl(null); setPage(1); }} selected={difficultyLevel === ""}>All Difficulties</MenuItem> */}
                        <MenuItem onClick={() => { setDifficultyLevel("EASY"); setAnchorEl(null); setPage(1); }} selected={difficultyLevel === "EASY"}>Easy</MenuItem>
                        <MenuItem onClick={() => { setDifficultyLevel("MEDIUM"); setAnchorEl(null); setPage(1); }} selected={difficultyLevel === "MEDIUM"}>Medium</MenuItem>
                        <MenuItem onClick={() => { setDifficultyLevel("HARD"); setAnchorEl(null); setPage(1); }} selected={difficultyLevel === "HARD"}>Hard</MenuItem>
                    </Menu>
                </div>
            }
        >
            <Table
                    columns={columns}
                    data={Array.isArray(challenges) ? challenges : []}
                    progressPending={loading}
                />
                <Pagination
                    totalCount={totalRecords}
                    pageSelected={page}
                    setPageSelected={setPage}
                    rowsPerPageValue={perPage}
                />

            {/* Modals */}
            {openModal === "io" && (
                <IOModal
                    onClose={() => {
                        setOpenModal(null);
                        setEditItem(null); // ✅ clear edit on close
                    }}
                    editItem={editItem} // ✅ pass edit data (null = create mode)
                    onUpload={async (data) => {
                        try {
                            if (editItem) {
                                console.log(editItem?.data?.id);

                                // ✅ Edit mode — call update API
                                await updateChallengesByAPI(data);
                                toast.success("Challenge updated successfully");
                            } else {
                                // ✅ Create mode — call create API
                                await createChallengesAPI(data);
                                toast.success("Challenge created successfully");
                            }
                            handleUpload();
                            dispatch(fetchChallenges({ pageNo: page, perPage, search: debouncedSearch }));
                        } catch (err) {
                            console.error(err);
                            toast.error(editItem ? "Failed to update challenge" : "Failed to create challenge");
                        }
                    }}
                />
            )}

            {openModal === "db" && (
                <DBModal
                    onClose={() => {
                        setOpenModal(null);
                        setEditItem(null);
                    }}
                    editItem={editItem}
                    onUpload={async (data) => {
                        try {
                            if (editItem) {
                                console.log(editItem);
                                console.log(editItem?.data?.id);

                                await updateChallengesByAPI(data);
                                toast.success("Challenge updated successfully");
                            } else {
                                await createChallengesAPI(data);
                                toast.success("Challenge created successfully");
                            }
                            handleUpload();
                            dispatch(fetchChallenges({ pageNo: page, perPage, search: debouncedSearch }));
                        } catch (err) {
                            console.error(err);
                            toast.error(editItem ? "Failed to update challenge" : "Failed to create challenge");
                        }
                    }}
                />
            )}

            <ConfirmDeleteModal
                deleteLabel="Challenge"
                handleDelete={handleDelete}
            />
        </PageLayout>
    );
};

export default Challenges;